import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Heart, Star, Users, Zap, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import CourseCover from '../components/course/CourseCover';
import { getStudentCount } from '../lib/studentCount';
import { hasRating } from '../lib/courseRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FavoritesPage() {
  const { lang, getField, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, { headers: authHeaders() });
      setFavorites(res.data.favorites);
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, lang));
    }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFavorites(); }, []);

  const removeFavorite = async (courseId, courseTitle) => {
    const confirmMsg = courseTitle
      ? (lang === 'tr'
          ? `"${courseTitle}" kursunu favorilerden çıkarmak istediğinize emin misiniz?`
          : `Remove "${courseTitle}" from favorites?`)
      : (lang === 'tr' ? 'Favorilerden çıkarmak istediğinize emin misiniz?' : 'Remove from favorites?');

    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.delete(`${API}/favorites/${courseId}`, { headers: authHeaders() });
      setFavorites(prev => prev.filter(f => f.course_id !== courseId));
      toast.success(lang === 'tr' ? 'Favorilerden cikarildi' : 'Removed from favorites');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-app)] py-8" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-bold mb-8 flex items-center gap-3">
          <Heart size={28} strokeWidth={2.5} className="text-[var(--brand-danger)]" />
          {lang === 'tr' ? 'Favori Kurslarim' : 'My Favorites'}
        </h1>

        {favorites.length === 0 ? (
          <div className="card-clay p-12 text-center">
            <Heart size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
            <p className="font-display text-lg font-bold text-[var(--text-muted)] mb-4">
              {lang === 'tr' ? 'Henuz favori kursunuz yok' : 'No favorite courses yet'}
            </p>
            <Link to="/courses" className="btn-primary px-8 py-3 inline-block" data-testid="browse-courses-link">
              {t('browse_courses')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((fav, i) => {
              const course = fav.course;
              if (!course) return null;
              return (
                <motion.div key={fav.course_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                  <div className="card-clay overflow-hidden relative" data-testid={`favorite-course-${i}`}>
                    <button onClick={() => removeFavorite(course.course_id, getField(course, 'title'))}
                      className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-sm hover:bg-red-50"
                      data-testid={`remove-fav-${i}`}>
                      <Trash2 size={14} className="text-[var(--brand-danger)]" />
                    </button>
                    <Link to={`/courses/${course.course_id}`}>
                      <CourseCover
                        thumbnail={course.thumbnail}
                        category={course.category}
                        alt={getField(course, 'title')}
                        className="aspect-video"
                        imgClassName="group-hover:scale-105 transition-transform"
                      >
                        {course.is_interactive && (
                          <span className="absolute top-3 left-3 bg-[var(--brand-secondary)] text-[var(--text-main)] text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Zap size={12} strokeWidth={2.5} /> {t('interactive_course')}
                          </span>
                        )}
                      </CourseCover>
                      <div className="p-5">
                        <h3
                          className="font-display font-bold text-base mb-2 line-clamp-2 overflow-hidden leading-snug min-h-[2.75rem]"
                          title={getField(course, 'title')}
                        >{getField(course, 'title')}</h3>
                        <div className="flex items-center gap-2 mb-3">
                          {hasRating(course) && (
                            <>
                              <Star size={14} className="text-[var(--brand-secondary)]" fill="var(--brand-secondary)" />
                              <span className="text-sm font-bold">{course.rating_avg ?? '—'}</span>
                            </>
                          )}
                          <span className="text-xs text-[var(--text-muted)] ml-auto flex items-center gap-1">
                            <Users size={12} /> {getStudentCount(course)}
                          </span>
                        </div>
                        <span className="font-display font-bold text-lg text-[var(--brand-primary)]">
                          {course.price > 0 ? `${course.price} ${course.currency}` : t('free')}
                        </span>
                      </div>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
